'use client'

/**
 * Bottom-left "nuke" button for on-device debugging — wipes IndexedDB
 * (intent queue, PowerSync mirror, offline blobs), Cache Storage, all SW
 * registrations, then signs out and reloads to /login. Useful on iOS where
 * the user can't open devtools to clear storage manually.
 */

import { useEffect, useState } from 'react'
import { usePathname } from '@/hooks/usePathname'
import { useAuth } from '@/components/AuthProvider'
import { createClient } from '@/utils/supabase/client'
import { closePowerSync } from '@/lib/powersync'
import { closeDB } from '@/lib/db'
import { getIntentWorker } from '@/lib/intent-worker'
import { isMarketingPath } from '@/lib/route-gates'

// Actual IDB databases this app creates:
//   imki-db       — intent queue + offline blobs (src/lib/db.ts)
//   imki.sqlite   — PowerSync wa-sqlite backing store (matches dbFilename)
//   PowerSyncFiles — PowerSync WASM/asset cache
//   imki-storage  — legacy pre-Postgres store (migrate-indexeddb-to-postgres.js)
// If any of these are renamed, update here too, otherwise Safari's
// under-reported databases() enumeration leaves stale data behind.
const KNOWN_DBS = ['imki-db', 'imki.sqlite', 'PowerSyncFiles', 'imki-storage']

// @supabase/ssr browser cookies are NOT HttpOnly (the client needs to read
// them), so we can delete them from JS. Wildcard match: any cookie starting
// with "sb-" plus the supabase project ref. Belt-and-braces — explicit names
// + everything-else fallback.
function clearAllCookies(): string[] {
  const cleared: string[] = []
  if (typeof document === 'undefined') return cleared
  const cookies = document.cookie.split(';').map((c) => c.trim()).filter(Boolean)
  // Each cookie has to be cleared on every plausible (path, domain) combo —
  // browsers won't delete a cookie unless the attributes match the set.
  const host = window.location.hostname
  const variants: Array<{ path: string; domain?: string }> = [
    { path: '/' },
    { path: '/', domain: host },
    { path: '/', domain: `.${host}` },
  ]
  for (const cookie of cookies) {
    const name = cookie.split('=')[0]
    if (!name) continue
    for (const v of variants) {
      const domain = v.domain ? `; domain=${v.domain}` : ''
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${v.path}${domain}; SameSite=Lax`
    }
    cleared.push(name)
  }
  return cleared
}

async function deleteAllIndexedDB(): Promise<string[]> {
  const deleted: string[] = []
  // Modern browsers: enumerate. Safari did not implement databases() until
  // recently — fall back to the known list if it's missing or returns nothing.
  let names: string[] = []
  try {
    const factory = indexedDB as IDBFactory & { databases?: () => Promise<{ name?: string }[]> }
    if (typeof factory.databases === 'function') {
      const list = await factory.databases()
      names = list.map((d) => d.name).filter((n): n is string => !!n)
    }
  } catch { /* fall through to known list */ }
  if (names.length === 0) names = [...KNOWN_DBS]
  // Always include known names too, in case databases() under-reports.
  for (const n of KNOWN_DBS) if (!names.includes(n)) names.push(n)

  // One quick attempt per DB. If onblocked fires (a SharedWorker still holds
  // the handle), we resolve immediately and leave the retry work to
  // runPostNukeCleanup() on the fresh /login page, where the old tab context
  // is gone and the delete succeeds cleanly. A long retry loop here caused
  // iOS App to get backgrounded/killed before window.location.replace() ran,
  // so the reload "never happened" on mobile.
  async function deleteOnce(name: string, timeoutMs: number): Promise<'deleted' | 'blocked' | 'error'> {
    return new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(name)
      let settled = false
      const settle = (v: 'deleted' | 'blocked' | 'error') => {
        if (settled) return
        settled = true
        resolve(v)
      }
      req.onsuccess = () => settle('deleted')
      req.onerror = () => settle('error')
      req.onblocked = () => settle('blocked')
      setTimeout(() => settle('blocked'), timeoutMs)
    })
  }
  await Promise.all(names.map(async (name) => {
    const result = await deleteOnce(name, 800)
    if (result === 'deleted') deleted.push(name)
  }))
  return deleted
}

// Heavier retry variant used only by the post-nuke cleanup pass on /login,
// where no worker should be holding handles open but we still want to recover
// from transient onblocked (SW teardown not quite done, etc).
async function deleteAllIndexedDBWithRetries(): Promise<string[]> {
  const deleted: string[] = []
  let names: string[] = []
  try {
    const factory = indexedDB as IDBFactory & { databases?: () => Promise<{ name?: string }[]> }
    if (typeof factory.databases === 'function') {
      const list = await factory.databases()
      names = list.map((d) => d.name).filter((n): n is string => !!n)
    }
  } catch { /* fall through to known list */ }
  if (names.length === 0) names = [...KNOWN_DBS]
  for (const n of KNOWN_DBS) if (!names.includes(n)) names.push(n)

  async function deleteOnce(name: string): Promise<'deleted' | 'blocked' | 'error'> {
    return new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(name)
      let settled = false
      const settle = (v: 'deleted' | 'blocked' | 'error') => {
        if (settled) return
        settled = true
        resolve(v)
      }
      req.onsuccess = () => settle('deleted')
      req.onerror = () => settle('error')
      req.onblocked = () => { /* wait for onsuccess or timeout */ }
      setTimeout(() => settle('blocked'), 1500)
    })
  }
  await Promise.all(names.map(async (name) => {
    for (let attempt = 0; attempt < 5; attempt++) {
      const result = await deleteOnce(name)
      if (result === 'deleted') { deleted.push(name); return }
      if (result === 'error') return
      await new Promise((r) => setTimeout(r, 300))
    }
  }))
  return deleted
}

async function nuke(): Promise<void> {
  const log: string[] = []
  try {
    // Order matters:
    //  1) supabase.auth.signOut (local + global), which clears cookies via
    //     server response. Use the supabase client directly — NEVER the
    //     AuthProvider's signOut, because that helper redirects to '/' and
    //     would abandon the rest of the cleanup mid-flight.
    //  2) Manual cookie sweep — defense in case signOut's network call failed
    //     (offline, or supabase API hiccup).
    //  3) Caches + storage.
    //  4) Close every open IDB handle (PowerSync worker, intent-queue db,
    //     intent worker) THEN deleteDatabase. Otherwise `onblocked` fires, the
    //     3s timeout resolves, and the DB survives the redirect.
    //  5) Hard navigation to /login.

    try {
      const client = createClient()
      // global tries to clear server-side too; local guarantees client state
      // resets even when offline.
      await client.auth.signOut({ scope: 'global' }).catch(() => {})
      await client.auth.signOut({ scope: 'local' }).catch(() => {})
      log.push('supabase signOut ok')
    } catch (err) { log.push(`auth.signOut: ${String(err)}`) }

    try {
      const cleared = clearAllCookies()
      log.push(`cleared cookies: ${cleared.join(',') || '(none)'}`)
    } catch (err) { log.push(`cookies: ${String(err)}`) }

    if ('caches' in self) {
      try {
        const keys = await caches.keys()
        await Promise.all(keys.map((k) => caches.delete(k)))
        log.push(`cleared ${keys.length} cache(s)`)
      } catch (err) { log.push(`caches.delete: ${String(err)}`) }
    }

    try { localStorage.clear(); sessionStorage.clear() } catch { /* noop */ }

    try { getIntentWorker().stop() } catch (err) { log.push(`worker.stop: ${String(err)}`) }
    try { await closePowerSync(); log.push('powersync closed') } catch (err) { log.push(`powersync.close: ${String(err)}`) }
    try { closeDB(); log.push('imki-db closed') } catch (err) { log.push(`imki-db.close: ${String(err)}`) }

    // PowerSync runs wa-sqlite in a SharedWorker; close() returns before the
    // worker has actually released its IDB file handle. Without this settle
    // delay, deleteDatabase('imki.sqlite') hits onblocked and the DB survives.
    await new Promise((r) => setTimeout(r, 250))

    try {
      const deleted = await deleteAllIndexedDB()
      log.push(`deleted IDB: ${deleted.join(', ') || '(none)'}`)
      // Verify — report any survivors so we see failed deletes in the log.
      try {
        const factory = indexedDB as IDBFactory & { databases?: () => Promise<{ name?: string }[]> }
        if (typeof factory.databases === 'function') {
          const remaining = (await factory.databases()).map((d) => d.name).filter(Boolean)
          if (remaining.length) log.push(`IDB survivors: ${remaining.join(', ')}`)
        }
      } catch { /* noop */ }
    } catch (err) { log.push(`IDB delete: ${String(err)}`) }

    console.log('[debug-nuke]', log.join(' | '))
    // Persist the log across the redirect. sessionStorage.clear() already ran
    // above, so this write survives to /login?nuked=... and the followup pass
    // picks it up and re-logs it (so the user can see what happened).
    try { sessionStorage.setItem('__nuke_log', log.join(' | ')) } catch { /* noop */ }
  } finally {
    // Hard reload to /login. `replace` keeps the back-button from returning
    // to a half-wiped page; the `?nuked=<ts>` query param busts any HTTP or
    // SW-cached `/login` shell so the browser hits the network for a clean
    // first paint post-wipe. The followup pass in runPostNukeCleanup() finishes
    // any IDB delete that was blocked by a lingering SharedWorker.
    window.location.replace(`/login?nuked=${Date.now()}`)
  }
}

// Second-pass cleanup on the fresh /login page. When a SharedWorker (e.g.
// PowerSync's wa-sqlite worker) held an IDB handle open during nuke(),
// deleteDatabase() hits onblocked and the DB survives the redirect. By the
// time this pass runs, the old tab context is gone and the worker has been
// torn down, so the delete succeeds cleanly.
async function runPostNukeCleanup(): Promise<void> {
  const prior = (() => { try { return sessionStorage.getItem('__nuke_log') } catch { return null } })()
  if (prior) {
    console.log('[debug-nuke:prior]', prior)
    try { sessionStorage.removeItem('__nuke_log') } catch { /* noop */ }
  }
  try {
    const deleted = await deleteAllIndexedDBWithRetries()
    console.log('[debug-nuke:followup] deleted', deleted.join(', ') || '(none)')
    const factory = indexedDB as IDBFactory & { databases?: () => Promise<{ name?: string }[]> }
    if (typeof factory.databases === 'function') {
      const remaining = (await factory.databases()).map((d) => d.name).filter(Boolean)
      if (remaining.length) console.warn('[debug-nuke:followup] survivors', remaining)
    }
    // Strip the ?nuked= flag so a real reload by the user won't skip PowerSync
    // init again. Use replaceState so we don't trigger another navigation.
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has('nuked')) {
        url.searchParams.delete('nuked')
        window.history.replaceState({}, '', url.toString())
      }
    } catch { /* noop */ }
  } catch (err) { console.warn('[debug-nuke:followup] failed', err) }
}

export function DebugNukeButton() {
  const { user } = useAuth()
  const pathname = usePathname()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!user) return null
  if (isMarketingPath(pathname)) return null
  if (process.env.NODE_ENV === 'production' && process.env.VITE_ENABLE_DEBUG_TOOLS !== '1') return null

  const onClick = async () => {
    if (!confirming) { setConfirming(true); return }
    setBusy(true)
    await nuke()
  }

  return (
    <div
      // Mobile: lift above the md:hidden tab bar (h-14 + safe-area, z-50) so the
      // button isn't buried under it. md+: no tab bar, revert to the low spot.
      className="fixed left-3 z-40 bottom-[calc(4rem_+_env(safe-area-inset-bottom))] md:bottom-[calc(0.75rem_+_env(safe-area-inset-bottom))]"
      style={{ paddingLeft: 'env(safe-area-inset-left)' }}
    >
      <button
        type="button"
        onClick={onClick}
        onBlur={() => setConfirming(false)}
        disabled={busy}
        aria-label="Debug: clear storage and sign out"
        className={`text-[10px] sm:text-xs rounded-full px-2.5 py-1 backdrop-blur-sm shadow-sm transition-colors font-mono ${
          confirming
            ? 'bg-red-600/90 hover:bg-red-700 text-white'
            : 'bg-gray-900/40 hover:bg-gray-900/70 text-white/70 hover:text-white'
        } ${busy ? 'opacity-60 cursor-wait' : ''}`}
      >
        {busy ? 'wiping…' : confirming ? 'tap again to wipe' : 'debug: nuke'}
      </button>
    </div>
  )
}

// Mount once in the root layout — runs on every page load. When `?nuked=` is
// present (fresh post-nuke navigation), fires the second-pass IDB cleanup to
// finish deletes that were blocked by a lingering SharedWorker in the prior
// tab context.
export function PostNukeCleanup() {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!new URLSearchParams(window.location.search).has('nuked')) return
    void runPostNukeCleanup()
  }, [])
  return null
}
